import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { personService } from '@shared/services/personService';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import useLocations from '@modules/organizationalStructure/locations/hooks/useLocations';

const normalizeSexValue = (value) => {
    if (value === undefined || value === null || value === '') {
        return 'female';
    }

    const normalized = value.toString().toLowerCase();
    if (['male', 'мъж', 'm', 'м', 'man'].includes(normalized)) {
        return 'male';
    }
    if (['female', 'жена', 'f', 'ж', 'woman'].includes(normalized)) {
        return 'female';
    }
    return normalized;
};

const toBooleanString = (value, fallback = 'true') => {
    if (value === undefined || value === null || value === '') {
        return fallback;
    }

    if (typeof value === 'boolean') {
        return value ? 'true' : 'false';
    }

    const normalized = value.toString().toLowerCase();
    if (['true', '1', 'yes', 'y'].includes(normalized)) {
        return 'true';
    }
    if (['false', '0', 'no', 'n'].includes(normalized)) {
        return 'false';
    }
    return normalized;
};

const extractIdnValue = (source) => {
    if (!source) return '';
    if (source.idn && typeof source.idn === 'object') {
        return source.idn.idn ?? '';
    }
    if (source.idn !== undefined && source.idn !== null) {
        return source.idn;
    }
    return '';
};

const extractIdentTypeValue = (source) => {
    if (!source) return 0;
    if (source.idn && typeof source.idn === 'object' && source.idn.identType !== undefined) {
        return Number(source.idn.identType);
    }
    if (source.identType !== undefined && source.identType !== null) {
        return Number(source.identType);
    }
    return 0;
};

const buildAddressLocation = (source) => {
    if (!source) return null;

    if (source.addressCityId && typeof source.addressCityId === 'object') {
        return {
            id: source.addressCityId.id ?? '',
            countryId: source.addressCityId.countryId ?? source.addressCountryId ?? source.country ?? '',
            regionId: source.addressCityId.regionId ?? source.addressRegionId ?? source.region ?? '',
            subregionId: source.addressCityId.subregionId ?? source.addressSubRegionId ?? source.subregion ?? ''
        };
    }

    const id = source.addressCityId ?? '';
    const countryId = source.addressCountryId ?? source.country ?? '';
    const regionId = source.addressRegionId ?? source.region ?? '';
    const subregionId = source.addressSubRegionId ?? source.subregion ?? '';

    if (!id && !countryId && !regionId && !subregionId) {
        return null;
    }

    return { id, countryId, regionId, subregionId };
};

const normalizePersonForState = (person) => {
    if (!person) return null;

    const addressLocation = buildAddressLocation(person);

    return {
        ...person,
        idn: extractIdnValue(person),
        identType: extractIdentTypeValue(person),
        sex: normalizeSexValue(person.sex),
        bgCitizenship: toBooleanString(person.bgCitizenship),
        addressCityId: addressLocation,
        addressCountryId: addressLocation?.countryId ?? person.addressCountryId ?? person.country ?? '',
        addressRegionId: addressLocation?.regionId ?? person.addressRegionId ?? person.region ?? '',
        addressSubRegionId: addressLocation?.subregionId ?? person.addressSubRegionId ?? person.subregion ?? '',
    };
};

const buildDefaultValues = (person, formType, t) => {
    const normalized = normalizePersonForState(person) || {};
    const defaultNationality = normalized.nationality ?? t('users.table.headers.bgNationality');

    const defaults = {
        name: normalized.name ?? '',
        fathersName: normalized.fathersName ?? '',
        surname: normalized.surname ?? '',
        foreignName: normalized.foreignName ?? '',
        foreignSurname: normalized.foreignSurname ?? '',
        foreignFathersName: normalized.foreignFathersName ?? '',
        pensioner: normalized.pensioner ?? false,
        invalid: normalized.invalid ?? false,
        idBdsk: normalized.idBdsk ?? '',
        dateOfBirth: normalized.dateOfBirth ?? '',
        sex: normalized.sex ?? 'female',
        idn: normalized.idn ?? '',
        identType: normalized.identType ?? 0,
        identityCardNo: normalized.identityCardNo ?? '',
        identityCardExpDate: normalized.identityCardExpDate ?? '',
        identityCardPublished: normalized.identityCardPublished ?? '',
        address: normalized.address ?? '',
        phone: normalized.phone ?? '',
        mobile: normalized.mobile ?? '',
        country: normalized.country ?? normalized.addressCountryId ?? '',
        region: normalized.region ?? normalized.addressRegionId ?? '',
        subregion: normalized.subregion ?? normalized.addressSubRegionId ?? '',
        addressCityId: normalized.addressCityId?.id ?? '',
        addressCountryId: normalized.addressCountryId ?? normalized.country ?? null,
        addressRegionId: normalized.addressRegionId ?? normalized.region ?? null,
        addressSubRegionId: normalized.addressSubRegionId ?? normalized.subregion ?? null,
        bgCitizenship: normalized.bgCitizenship ?? 'true',
        active: normalized.active ?? true,
        birthplaceCountry: normalized.birthplaceCountry ?? '',
        birthplace: normalized.birthplace ?? '',
        personPhoto: normalized.personPhoto ?? '',
        idnOrOther: normalized.idnOrOther ?? 'select',
        familyStatus: normalized.familyStatus ?? '',
        nationality: defaultNationality,
        postAddressCountryId: normalized.postAddressCountryId ?? 'BG',
        postAddressRegionId: normalized.postAddressRegionId ?? null,
        postAddressSubRegionId: normalized.postAddressSubRegionId ?? null,
        postAddressCityId: normalized.postAddressCityId ?? null,
        postAddress: normalized.postAddress ?? null,
    };

    if (!defaults.country) {
        defaults.country = 'BG';
    }

    if (defaults.addressCountryId === null) {
        defaults.addressCountryId = formType === 'documents' ? null : 'BG';
    }

    if (formType === 'documents') {
        defaults.address = normalized.postAddress ?? '';
        defaults.country = normalized.postAddressCountryId ?? 'BG';
        defaults.region = normalized.postAddressRegionId ?? '';
        defaults.subregion = normalized.postAddressSubRegionId ?? '';
        defaults.addressCityId = normalized.postAddressCityId ?? '';
    } else {
        delete defaults.postAddressCountryId;
        delete defaults.postAddressRegionId;
        delete defaults.postAddressSubRegionId;
        delete defaults.postAddressCityId;
        delete defaults.postAddress;
        delete defaults.foreignName;
        delete defaults.foreignSurname;
        delete defaults.foreignFathersName;
    }

    return defaults;
};

const extractPersonEntity = (data) => {
    if (!data) return null;

    if (data.person) {
        return data.person;
    }

    if (data.personData) {
        return data.personData;
    }

    if (data.data && data.data.person) {
        return data.data.person;
    }

    return data;
};

export const useDocumentsForm = (person, t, formType, fetchData, onClose, identType) => {
    const initialNormalizedPerson = useMemo(() => normalizePersonForState(person), [person]);

    const [serverMessage, setServerMessage] = useState(null);
    const [photoUrl, setPhotoUrl] = useState(
        initialNormalizedPerson?.personPhoto || '/images/person/noimage.png'
    );
    const [selectedFile, setSelectedFile] = useState(null);
    const [personData, setPersonData] = useState(initialNormalizedPerson);
    const [nationality, setNationality] = useState(() => {
        if (formType === 'documents') {
            return initialNormalizedPerson?.nationality ?? t('users.table.headers.bgNationality');
        }
        return initialNormalizedPerson?.nationality ?? '';
    });
    const [familyStatus, setFamilyStatus] = useState(
        initialNormalizedPerson?.familyStatus ?? ''
    );
    const [documentsResponse, setDocumentsResponse] = useState(null);
    const { countryOptions, fetchCountries } = useLocations();
    const debounceTimeout = useRef(null);
    const [customErrors, setCustomErrors] = useState({});

    const defaultImagePath = '/images/person/noimage.png';

    const bulgarianNameRegex = /^[А-Яа-я\s-]+$/;  // Allows Bulgarian letters, spaces, and hyphens
    const englishNameRegex = /^[A-Za-z\s]+$/;


    const validationSchema = yup.object({
        ...(formType === 'main' && {
            idnOrOther: yup.string(),
            idn: yup.string()
                .required(t('validation.person.idn.required'))
                .length(10, t('validation.person.idn.length'))
                .matches(/^\d{10}$/, t('validation.person.idn.format')),
            identityCardNo: yup.string()
                .required(t('validation.person.identifyCardRequired'))
                .matches(
                    /^(?:\d{9}|[A-Za-z]{2}\d{7})$/,
                    t('validation.person.identityCardNo.format')
                ),
            identityCardPublished: yup.string()
                .min(1, t('validation.person.identityCardPublished.size'))
                .max(60, t('validation.person.identityCardPublished.size'))
                .nullable(),
            phone: yup.string()
                .matches(/^[0-9\s-+()]*$/, t('validation.person.phone.format'))
                .max(60, t('validation.person.phone.size')),
            mobile: yup.string()
                .matches(/^[0-9\s-+()]*$/, t('validation.person.mobile.format'))
                .max(60, t('validation.person.mobile.size')),
            name: yup.string()
                .required(t('validation.person.name.notBlank'))
                .min(1, t('validation.person.name.size'))
                .max(30, t('validation.person.name.size'))
                .matches(bulgarianNameRegex, t('validation.person.name.bulgarian')),
            fathersName: yup.string()
                .nullable()
                .transform((value) => (value === '' ? null : value))
                .test('bulgarian-letters', t('validation.person.fathersName.bulgarian'), function (value) {
                    if (!value) return true; // Skip validation if empty
                    return bulgarianNameRegex.test(value);
                }),
            surname: yup.string()
                .min(1, t('validation.person.surname.size'))
                .max(60, t('validation.person.surname.size'))
                .matches(bulgarianNameRegex, t('validation.person.surname.bulgarian'))
                .nullable(),
            dateOfBirth: yup.mixed()
                .test('is-required', t('validation.person.dateOfBirth.required'), function (value) {
                    return value !== '' && value !== null && value !== undefined;
                })
                .nullable(),
            birthplaceCountry: yup.string()
                .required(t('validation.person.birthplaceCountry.required')),
        }),
        address: yup.string()
            .required(t('validation.person.addressRequired')),
        ...(formType === 'documents' && {
            foreignName: yup.string()
                .required(t('validation.person.name.notBlank'))
                .min(1, t('validation.person.name.size'))
                .max(30, t('validation.person.name.size'))
                .matches(englishNameRegex, t('validation.person.name.english')),
            foreignFathersName: yup.string()
                .nullable()
                .transform((value) => (value === '' ? null : value))
                .test('english-letters', t('validation.person.fathersName.english'), function (value) {
                    if (!value) return true; // Skip validation if empty
                    return englishNameRegex.test(value);
                }),
            foreignSurname: yup.string()
                .min(1, t('validation.person.surname.size'))
                .max(60, t('validation.person.surname.size'))
                .matches(englishNameRegex, t('validation.person.surname.english'))
                .nullable(),
        }),
        birthplace: yup.string()
            .max(60, t('validation.person.address.size'))
            .nullable(),
        country: yup.string().nullable(),
        region: yup.string().nullable(),
        subregion: yup.string().nullable(),
        addressaddressCityId: yup.string().nullable(),
        bgCitizenship: yup.string()
            .oneOf(['true', 'false'])
            .nullable(),
        active: yup.boolean()
    });

    const initialDefaultValues = useMemo(
        () => buildDefaultValues(initialNormalizedPerson, formType, t),
        [initialNormalizedPerson, formType, t]
    );

    const {
        register,
        handleSubmit,
        setValue,
        reset,
        getValues,
        watch,
        setError,
        clearErrors: clearFormErrors,
        formState: { errors: formErrors }
    } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: initialDefaultValues
    });

    const fetchPersonData = useCallback(async () => {
        if (!person?.id) {
            return;
        }

        try {
            const fetchedData = formType === 'documents'
                ? await personService.getPersonAddData(person.id)
                : await personService.getPersonById(person.id);

            if (formType === 'documents') {
                setDocumentsResponse(fetchedData);
            }

            const personEntity = extractPersonEntity(fetchedData);
            const normalizedPerson = normalizePersonForState(personEntity);

            setPersonData(normalizedPerson);

            const defaults = buildDefaultValues(normalizedPerson, formType, t);
            reset(defaults);

            if (formType === 'documents') {
                setFamilyStatus(normalizedPerson?.familyStatus ?? '');
                setNationality(normalizedPerson?.nationality ?? t('users.table.headers.bgNationality'));
            }

            if (formType === 'documents') {
                try {
                    const photoResponse = await personService.getPersonPicture(person.id);
                    if (photoResponse) {
                        const imageUrl = URL.createObjectURL(photoResponse.data);
                        setPhotoUrl(imageUrl);
                        setValue('personPhoto', imageUrl);
                    } else {
                        setPhotoUrl(defaultImagePath);
                        setValue('personPhoto', defaultImagePath);
                    }
                } catch {
                    setPhotoUrl(defaultImagePath);
                    setValue('personPhoto', defaultImagePath);
                }
            }
        } catch (error) {
            console.error('Error fetching person data:', error);
            notifyError(t('persons.documents.fetchError'));
            if (formType === 'documents') {
                setPhotoUrl(defaultImagePath);
                setValue('personPhoto', defaultImagePath);
            }
        }
    }, [person?.id, formType, t, reset, setValue, defaultImagePath]);

    useEffect(() => {
        fetchPersonData();
    }, [fetchPersonData]);

    useEffect(() => {
        return () => {
            if (photoUrl && photoUrl !== defaultImagePath) {
                URL.revokeObjectURL(photoUrl);
            }
        };
    }, [photoUrl, defaultImagePath]);

    useEffect(() => {
        fetchCountries();
    }, [fetchCountries]);

    useEffect(() => {
        if (person) {
            const normalized = normalizePersonForState(person);
            setPersonData(normalized);
            const defaults = buildDefaultValues(normalized, formType, t);
            reset(defaults);

            if (formType === 'documents') {
                setFamilyStatus(normalized?.familyStatus ?? '');
                setNationality(normalized?.nationality ?? t('users.table.headers.bgNationality'));
            }
        } else {
            setPersonData(null);
            const defaults = buildDefaultValues(null, formType, t);
            reset(defaults);

            if (formType === 'documents') {
                setFamilyStatus('');
                setNationality(t('users.table.headers.bgNationality'));
            }
        }
    }, [person, formType, reset, t]);

    const handleIdnChange = (e) => {
        const idn = e.target.value;

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            // Basic validation for all types: must be digits only
            if (!/^\d*$/.test(idn)) {
                setError('idn', {
                    type: 'manual',
                    message: t('validation.person.idn.format')
                });
                return;
            }

            // Length validation for all types
            if (idn.length !== 10) {
                setError('idn', {
                    type: 'manual',
                    message: t('persons.form.idn-10')
                });
                return;
            }

            if (identType) {
                const year = parseInt(idn.substring(0, 2), 10);
                let month = parseInt(idn.substring(2, 4), 10);
                const day = parseInt(idn.substring(4, 6), 10);

                let fullYear;
                if (month > 40) {
                    fullYear = 2000 + year;
                    month -= 40;
                } else {
                    fullYear = 1900 + year;
                }

                if (month < 1 || month > 12 || day < 1 || day > 31) {
                    setError('idn', {
                        type: 'manual',
                        message: t('persons.form.idn-invalid')
                    });
                    return;
                }

                const date = new Date(fullYear, month - 1, day);
                if (date.getMonth() !== month - 1 || date.getDate() !== day) {
                    setError('idn', {
                        type: 'manual',
                        message: t('persons.form.idn-invalid')
                    });
                    return;
                }

                const dateOfBirth = `${fullYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                setValue('dateOfBirth', dateOfBirth);

                const genderDigit = parseInt(idn.charAt(8), 10);
                const gender = genderDigit % 2 === 0 ? 'male' : 'female';
                setValue('sex', gender);
            }

            clearFormErrors('idn');
        }, 500);
    };



    const generateRandomId = () => {
        const randomId = Math.random().toString(36).substring(7);
        setValue('idBdsk', randomId);
    };

    const familyStatusOptions = [
        { value: 'Неомъжена', label: t('persons.documents.singleF') },
        { value: 'Неомъжен', label: t('persons.documents.singleM') },
        { value: 'Омъжена', label: t('persons.documents.marriedF') },
        { value: 'Женен', label: t('persons.documents.marriedM') },
        { value: 'Разведена', label: t('persons.documents.divorcedF') },
        { value: 'Разведен', label: t('persons.documents.divorcedM') },
        { value: 'Вдовица', label: t('persons.documents.widowedF') },
        { value: 'Вдовец', label: t('persons.documents.widowedM') }
    ];

    const nationalityOptions = [
        { value: 'bulgarian', label: t('persons.nationality.bulgarian') },
        { value: 'other', label: t('persons.nationality.other') }
    ];

    const handleFamilyStatusChange = (selectedOption) => {
        if (formType === 'documents') {
            setValue('familyStatus', selectedOption.value);
        }
        setFamilyStatus(selectedOption.value);
    };

    const handleNationalityChange = (selectedOption) => {
        if (formType === 'documents') {
            setValue('nationality', selectedOption.label);
        }
        setNationality(selectedOption.label);
    };

    const handleCreatePerson = useCallback(async (data) => {
        try {
            await personService.createPerson(data);
            notifySuccess(t('persons.documents.createSuccess'));
            return true;
        } catch (error) {
            const message = error?.response?.data?.message;
            const errors = error?.response?.data?.errors;
            const errorMessage = error?.response?.data?.idn;

            if (errorMessage) {
                setError('idn', {
                    type: 'manual',
                    message: errorMessage
                });
                setCustomErrors(prev => ({
                    ...prev,
                    idn: {
                        type: 'manual',
                        message: errorMessage
                    }
                }));
            } else if (errors) {
                // Handle IDN specific errors
                if (errors.idn) {
                    setError('idn', {
                        type: 'manual',
                        message: errors.idn
                    });
                    setCustomErrors(prev => ({
                        ...prev,
                        idn: {
                            type: 'manual',
                            message: errors.idn
                        }
                    }));
                }
                if (errors['idn.idn']) {
                    setError('idn', {
                        type: 'manual',
                        message: errors['idn.idn']
                    });
                    setCustomErrors(prev => ({
                        ...prev,
                        idn: {
                            type: 'manual',
                            message: errors['idn.idn']
                        }
                    }));
                }
            }

            setServerMessage(message);
            notifyError(message);
            return false;
        }
    }, [setError, t]);

    const submitForm = async (data) => {
        try {
            if (person?.id) {
                const idnObject = {
                    idn: data.idn,
                    identType: data.identType
                };

                const modifiedData = {
                    ...data,
                    idn: idnObject
                };

                const skipKeysForDocuments = new Set(['country', 'region', 'subregion', 'addressCityId']);
                const postAddressKeys = new Set([
                    'postAddressCountryId',
                    'postAddressRegionId',
                    'postAddressSubRegionId',
                    'postAddressCityId',
                    'postAddress'
                ]);

                const payload = {};

                Object.keys(modifiedData).forEach(key => {
                    if (key === 'personPhoto' || key === 'selectedFile') {
                        return;
                    }

                    if (formType === 'documents' && skipKeysForDocuments.has(key)) {
                        return;
                    }

                    let value = modifiedData[key];

                    if (key === 'idn') {
                        const idnValue = value?.idn ?? '';
                        const identTypeValue = value?.identType ?? data.identType ?? 0;
                        const normalizedIdentType = Number.isNaN(Number(identTypeValue)) ? 0 : Number(identTypeValue);
                        payload.idn = {
                            idn: idnValue,
                            identType: normalizedIdentType
                        };
                        if (payload.identType === undefined) {
                            payload.identType = normalizedIdentType;
                        }
                        return;
                    }

                    if (key === 'identType') {
                        const normalizedIdentType = Number.isNaN(Number(value)) ? 0 : Number(value);
                        payload.identType = normalizedIdentType;
                        return;
                    }

                    if (key === 'nationality') {
                        payload.nationality = value || t('persons.nationality.bulgarian');
                        return;
                    }

                    if (key === 'familyStatus') {
                        payload.familyStatus = value || '';
                        return;
                    }

                    if (key === 'postAddressCountryId') {
                        payload.postAddressCountryId = value || 'BG';
                        return;
                    }

                    if (postAddressKeys.has(key)) {
                        payload[key] = value ?? null;
                        return;
                    }

                    if (key === 'dateOfBirth' || key === 'identityCardExpDate') {
                        value = value ? new Date(value).toISOString().split('T')[0] : null;
                    }

                    if (key === 'name' || key === 'fathersName' || key === 'surname' ||
                        key === 'foreignName' || key === 'foreignFathersName' || key === 'foreignSurname') {
                        value = value ? value.toUpperCase() : value;
                    }

                    payload[key] = value;
                });

                try {
                    const updateService = formType === 'documents'
                        ? personService.updatePersonAddData
                        : personService.updatePerson;

                    await updateService(person.id, payload);

                    if (selectedFile && formType === 'documents') {
                        try {
                            await personService.updatePersonPhoto(person.id, selectedFile);
                            setSelectedFile(null);
                        } catch (error) {
                            console.error('Error updating person photo:', error);
                            notifyError(t('persons.documents.updateError'));
                            return;
                        }
                    }

                    notifySuccess(
                        t(formType === 'documents' ? 'persons.documents.updateSuccess' : 'persons.documents.updateSuccess')
                    );
                    fetchData(personService.getPersons, {});
                    onClose();
                } catch (error) {
                    console.error('Error updating person:', error);
                    const errors = error?.response?.data?.errors;
                    const errorMessage = error?.response?.data?.idn;

                    if (errorMessage) {
                        setError('idn', {
                            type: 'manual',
                            message: errorMessage
                        });
                    } else if (errors) {
                        // Handle IDN specific errors
                        if (errors.idn) {
                            setError('idn', {
                                type: 'manual',
                                message: errors.idn
                            });
                        }
                        if (errors['idn.idn']) {
                            setError('idn', {
                                type: 'manual',
                                message: errors['idn.idn']
                            });
                        }
                    }
                    notifyError(t('persons.documents.updateError'));
                }
            } else {
                // Handle create case
                const createData = { ...data };

                // Transform text fields to uppercase
                if (createData.name) createData.name = createData.name.toUpperCase();
                if (createData.fathersName) createData.fathersName = createData.fathersName.toUpperCase();
                if (createData.surname) createData.surname = createData.surname.toUpperCase();
                if (createData.foreignName) createData.foreignName = createData.foreignName.toUpperCase();
                if (createData.foreignFathersName) createData.foreignFathersName = createData.foreignFathersName.toUpperCase();
                if (createData.foreignSurname) createData.foreignSurname = createData.foreignSurname.toUpperCase();

                // Format dates
                createData.dateOfBirth = createData.dateOfBirth ?
                    new Date(createData.dateOfBirth).toISOString().split('T')[0] : null;
                createData.identityCardExpDate = createData.identityCardExpDate ?
                    new Date(createData.identityCardExpDate).toISOString().split('T')[0] : null;

                // Set default values for required fields
                createData.nationality = createData.nationality || t('persons.nationality.bulgarian');
                createData.familyStatus = createData.familyStatus || '';

                // Format IDN object
                createData.idn = {
                    idn: createData.idn,
                    identType: Number.isInteger(Number(createData.identType)) ? Number(createData.identType) : 0
                };

                if (formType === 'documents') {
                    createData.postAddressCountryId = createData.postAddressCountryId || null;
                    createData.postAddressRegionId = createData.postAddressRegionId || null;
                    createData.postAddressSubRegionId = createData.postAddressSubRegionId || null;
                    createData.postAddressCityId = createData.postAddressCityId || null;
                    createData.postAddress = createData.postAddress || null;
                }

                // Remove unnecessary fields
                if (formType !== 'documents') {
                    delete createData.familyStatus;
                    delete createData.nationality;
                    delete createData.personPhoto;
                    delete createData.selectedFile;
                }

                try {
                    const success = await handleCreatePerson(createData);
                    if (success) {
                        fetchData(personService.getPersons, {});
                        onClose();
                    }
                } catch (error) {
                    console.error('Error fetching persons:', error);
                    notifyError(t('persons.documents.fetchError'));
                }
            }
        } catch (error) {
            console.error('Error updating person:', error);
            notifyError(t('persons.documents.updateError'));
        }
    };

    const handleImagePreview = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            if (photoUrl && photoUrl !== defaultImagePath) {
                URL.revokeObjectURL(photoUrl);
            }
            const imageUrl = URL.createObjectURL(file);
            setPhotoUrl(imageUrl);
        }
    };

    const onSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();

        handleSubmit(submitForm)(e);
    };

    const clearAllErrors = useCallback(() => {
        // Clear only IDN related errors from form state
        clearFormErrors('idn');
        // Clear only IDN related errors from custom state
        setCustomErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.idn;
            return newErrors;
        });
    }, [clearFormErrors]);

    return {
        register,
        handleSubmit,
        errors: formErrors,
        setValue,
        clearErrors: clearAllErrors,
        reset,
        getValues,
        watch,
        photoUrl,
        selectedFile,
        handleImagePreview,
        submitForm,
        personData,
        nationality,
        familyStatus,
        countryOptions,
        familyStatusOptions,
        nationalityOptions,
        generateRandomId,
        defaultImagePath,
        handleFamilyStatusChange,
        handleNationalityChange,
        onSubmit,
        handleIdnChange,
        serverMessage,
        documentsResponse,
    };
}; 